import * as React from 'react';
import propz from 'propz';
import { History, Location } from 'history';
import { useParams } from 'react-router-dom';
import { AppUser } from '../../../../../views/App/App';
import { ProgrammeBookingContent } from '../../../../../views/GenericView/components/ClubProgrammes/ProgrammeBooking/ProgrammeBookingContent';
import { ProgrammeBookingContentProps } from '../../../../../views/GenericView/components/ClubProgrammes/ProgrammeBooking/ProgrammeBooking.contracts';
import { buildProgrammeBookingContentProps } from '../../../../../views/GenericView/components/ClubProgrammes/ProgrammeBooking/buildProgrammeBookingContentProps';
import {
  ProgrammeBookingInvitesResponse,
  ProgrammeBookingDraftPayload,
  StudentProgrammeBookingConfirmPayload
} from '../../../../../views/GenericView/components/ClubProgrammes/ProgrammeBooking/ProgrammeBooking.types';
import {
  getStudentProgrammeBookingInvites,
  draftStudentProgrammeClubInvite,
  undraftStudentProgrammeClubInvite,
  updateAcceptedStudentProgrammeClubInvite,
  cancelStudentProgrammeClubInvite,
  confirmStudentProgrammeBooking
} from '../../../../../helpers/service/student/clubProgrammesBooking';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

type Params = { programmeId: string };

export const ProgrammeBooking: React.FC<Props> = props => {
  const { user, history } = props;
  const { programmeId } = useParams<Params>();

  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<ProgrammeBookingInvitesResponse | null>(null);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const actorLabel = React.useMemo(() => {
    const firstName = String(propz.get(user, ['firstName'], '') || '').trim();
    const lastName = String(propz.get(user, ['lastName'], '') || '').trim();
    return `${firstName} ${lastName}`.trim();
  }, [user]);

  const playerPermissionId = React.useMemo(() => {
    const search = String(propz.get(props, ['location', 'search'], '') || '');
    const params = new URLSearchParams(search);
    return String(params.get('playerPermissionId') || '');
  }, [props.location]);

  const load = React.useCallback(async () => {
    try {
      setError(undefined);
      setIsLoading(true);

      if (!programmeId) {
        setData(null);
        setError('programmeId is missing in URL');
        return;
      }

      if (!playerPermissionId) {
        setData(null);
        setError('Student permission is missing');
        return;
      }

      const res = await getStudentProgrammeBookingInvites(user, programmeId, playerPermissionId);
      setData(res);
    } catch (e) {
      const msg =
        String(
          propz.get(
            e,
            ['response', 'data', 'error'],
            propz.get(e, ['response', 'data', 'message'], propz.get(e, ['message'], 'Failed to load programme booking'))
          )
        ) || 'Failed to load programme booking';

      setError(msg);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [user, programmeId, playerPermissionId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleDraftClub = React.useCallback(
    async (row: any, payload: ProgrammeBookingDraftPayload) => {
      const messageId = String(propz.get(row, ['invite', 'id'], ''));
      return draftStudentProgrammeClubInvite(user, messageId, payload);
    },
    [user]
  );

  const handleUndraftClub = React.useCallback(
    async (row: any) => {
      const messageId = String(propz.get(row, ['invite', 'id'], ''));
      return undraftStudentProgrammeClubInvite(user, messageId);
    },
    [user]
  );

  const handleUpdateAcceptedClub = React.useCallback(
    async (row: any, payload: ProgrammeBookingDraftPayload) => {
      const messageId = String(propz.get(row, ['invite', 'id'], ''));
      return updateAcceptedStudentProgrammeClubInvite(user, messageId, payload);
    },
    [user]
  );

  const handleCancelAcceptedClub = React.useCallback(
    async (row: any) => {
      const messageId = String(propz.get(row, ['invite', 'id'], ''));
      return cancelStudentProgrammeClubInvite(user, messageId);
    },
    [user]
  );

  const handleConfirmProgramme = React.useCallback(
    async (payload: StudentProgrammeBookingConfirmPayload) => {
      return confirmStudentProgrammeBooking(user, programmeId, payload);
    },
    [user, programmeId]
  );

  const contentProps: ProgrammeBookingContentProps = buildProgrammeBookingContentProps({
    user,
    history,
    actorLabel,
    actorFallbackLabel: 'You',
    data,
    isLoading,
    error,
    onReload: load,
    onDraftClub: handleDraftClub,
    onUndraftClub: handleUndraftClub as any,
    onUpdateAcceptedClub: handleUpdateAcceptedClub,
    onCancelAcceptedClub: handleCancelAcceptedClub as any,
    onConfirmProgramme: ((() =>
      handleConfirmProgramme({ playerPermissionId })) as unknown) as ProgrammeBookingContentProps['onConfirmProgramme']
  });

  return <ProgrammeBookingContent {...contentProps} />;
};

